import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import BottomNav from "../components/BottomNav";
import { Button } from "../components/ui/button";
import { Star, Check, Zap, Eye, Sparkles, Crown, Calendar, ArrowLeft, AlertCircle } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import ReviewPromptModal from "../components/ReviewPromptModal";
import { useReviewPrompt } from "../hooks/useReviewPrompt";
import { useDigitalGoods } from "../hooks/useDigitalGoods";
import { usePurchase } from "../hooks/usePurchase";
import { PACKS, PLANS, PLAN_FEATURES, isInstalledApp, isPlayStoreApp } from "../lib/products";
import { toast } from "sonner";

import { API } from "../config";
import { tx } from "../lib/tx";

// SKUs Google Play pour les crédits
const GOOGLE_PLAY_SKUS = {
  credits: ['credits_pack_small', 'credits_pack_medium', 'credits_pack_large'],
  subscriptions: ['premium_weekly', 'premium_monthly']
};

const PACKAGES = PACKS;

const SUBSCRIPTIONS_GOOGLE = PLANS.map((p) => ({
  ...p,
  name: p.id === "weekly" ? { fr: "Hebdomadaire", en: "Weekly" } : p.name,
  price: `${p.price}${p.period.fr}`,
  features: [...PLAN_FEATURES, { fr: `+${p.credits} crédits / ${p.duration.fr}`, en: `+${p.credits} credits / ${p.duration.en}` }],
}));

const FEATURES = [
  { icon: <Star className="w-5 h-5" />, label: "Super Like", cost: 5, color: "text-secondary" },
  { icon: <Zap className="w-5 h-5" />, label: "Boost Profil", cost: 20, color: "text-purple-500" },
  { icon: <Eye className="w-5 h-5" />, label: "Voir les Likes", cost: 5, color: "text-pink-500" }
];

export default function Credits() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { language } = useLanguage();
  const [selectedPackage, setSelectedPackage] = useState("medium");
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [boostLoading, setBoostLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("credits"); // "credits" or "subscription"
  const [googlePlayProducts, setGooglePlayProducts] = useState([]);
  const [purchaseError, setPurchaseError] = useState(null);
  const [packagesWithPrices, setPackagesWithPrices] = useState(PACKAGES);
  const [installedApp] = useState(() => isInstalledApp());

  // Digital Goods API hook pour Google Play Billing
  const { 
    isAvailable: isGooglePlayAvailable, 
    loading: googlePlayLoading,
    getDetails
  } = useDigitalGoods();

  // Review prompt hook
  const { 
    showReviewPrompt, 
    triggerSource, 
    triggerAfterPurchase,
    handleReviewResponse, 
    closePrompt: closeReviewPrompt 
  } = useReviewPrompt(user);

  // Charger les prix DYNAMIQUES depuis Google Play
  useEffect(() => {
    const loadGooglePlayPrices = async () => {
      if (!isGooglePlayAvailable) {
        console.log('Google Play Billing non disponible - prix non chargés');
        return;
      }

      try {
        const skus = PACKAGES.map(p => p.sku);
        const details = await getDetails(skus);
        
        // Mettre à jour avec les VRAIS prix Google Play (TTC + devise locale)
        const updatedPackages = PACKAGES.map(pkg => {
          const gpProduct = details.find(d => d.itemId === pkg.sku);
          if (gpProduct) {
            return {
              ...pkg,
              price: gpProduct.formattedPrice, // Prix EXACT Google Play
              priceRaw: gpProduct.price
            };
          }
          return pkg;
        });
        
        setPackagesWithPrices(updatedPackages);
        setGooglePlayProducts(details);
        console.log('Prix Google Play chargés (TTC + devise locale):', details);
      } catch (err) {
        console.error('Erreur chargement prix Google Play:', err);
      }
    };

    loadGooglePlayPrices();
  }, [isGooglePlayAvailable, getDetails]);

  // Version Play Store : pas de prix affichés (Google les affiche dans sa fenêtre de paiement)
  const showPrices = !(isGooglePlayAvailable || installedApp);

  const { buy } = usePurchase();

  // Retour du paiement web Mollie : le webhook crédite le compte, on rafraîchit le solde
  useEffect(() => {
    if (!new URLSearchParams(window.location.search).get("payment")) return;
    toast.success(language === 'fr' ? "Paiement reçu ! Vos crédits arrivent…" : "Payment received! Your credits are on the way…");
    const refresh = async () => {
      const r = await fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
      if (r.ok) setUser(await r.json());
    };
    const t1 = setTimeout(refresh, 3000);
    const t2 = setTimeout(refresh, 10000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [setUser, language]);

  const handlePurchase = async (id = selectedPackage) => {
    const pkg = packagesWithPrices.find(p => p.id === id);
    if (!pkg) return;
    setLoading(true);
    setPurchaseError(null);
    if (await buy(pkg.sku, false)) setTimeout(() => triggerAfterPurchase(), 1000);
    setLoading(false);
  };

  const handleSubscribe = async (id = selectedSubscription) => {
    const sub = SUBSCRIPTIONS_GOOGLE.find(s => s.id === id);
    if (!sub) return;
    setLoading(true);
    setPurchaseError(null);
    if (await buy(sub.sku, true)) setTimeout(() => triggerAfterPurchase(), 1000);
    setLoading(false);
  };

  const handleBoost = async () => {
    if ((user?.credits || 0) < 20) {
      alert(language === 'fr' ? "Pas assez de crédits (20 requis)" : "Not enough credits (20 required)");
      return;
    }

    setBoostLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/credits/boost`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` }
      });

      const data = await response.json();

      if (response.ok) {
        setUser(prev => ({ ...prev, credits: (prev?.credits || 0) - 20, is_boosted: true }));
        alert(language === 'fr' ? "Profil boosté pendant 30 minutes !" : "Profile boosted for 30 minutes!");
      } else {
        alert(data.detail || tx("Erreur"));
      }
    } catch (error) {
      console.error("Boost error:", error);
      alert(tx("Erreur lors du boost"));
    } finally {
      setBoostLoading(false);
    }
  };

  const handleDailyBonus = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/credits/daily-bonus`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` }
      });

      const data = await response.json();

      if (response.ok) {
        setUser(prev => ({ ...prev, credits: (prev?.credits || 0) + 10 }));
        alert(language === 'fr' ? "Bonus quotidien : +10 crédits !" : "Daily bonus: +10 credits!");
      } else {
        alert(data.detail || (language === 'fr' ? "Bonus déjà réclamé aujourd'hui" : "Bonus already claimed today"));
      }
    } catch (error) {
      console.error("Bonus error:", error);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-safe" data-testid="credits-page">
      
      <div className="pt-4 pb-24 px-4">
        <div className="max-w-lg mx-auto">
          {/* Header with back button */}
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-full bg-card/50 flex items-center justify-center text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-bold text-white">
              {language === 'fr' ? 'Boutique' : 'Shop'}
            </h1>
          </div>

          {/* Current Balance */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-3 px-6 py-4 bg-card/50 backdrop-blur-sm border border-white/5 rounded-3xl mb-4">
              <div className="w-12 h-12 rounded-xl gradient-gold flex items-center justify-center">
                <Star className="w-6 h-6 text-black fill-current" />
              </div>
              <div className="text-left">
                <p className="text-sm text-muted-foreground">
                  {language === 'fr' ? 'Solde actuel' : 'Current balance'}
                </p>
                <p className="text-3xl font-bold text-gradient-gold">{user?.credits || 0}</p>
              </div>
            </div>
            
            <Button
              onClick={handleDailyBonus}
              variant="outline"
              className="rounded-full border-secondary/50 text-secondary hover:bg-secondary/10"
              data-testid="daily-bonus-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Bonus quotidien (+10)' : 'Daily bonus (+10)'}
            </Button>
          </div>

          {/* Tab Selector */}
          <div className="flex gap-2 mb-6 bg-card/30 p-1 rounded-2xl">
            <button
              onClick={() => setActiveTab("credits")}
              className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
                activeTab === "credits"
                  ? "bg-primary text-white"
                  : "text-muted-foreground hover:text-white"
              }`}
            >
              <Star className="w-4 h-4 inline mr-2" />
              {language === 'fr' ? 'Pièces' : 'Coins'}
            </button>
            <button
              onClick={() => setActiveTab("subscription")}
              className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
                activeTab === "subscription"
                  ? "bg-primary text-white"
                  : "text-muted-foreground hover:text-white"
              }`}
            >
              <Crown className="w-4 h-4 inline mr-2" />
              {language === 'fr' ? 'Abonnement' : 'Subscription'}
            </button>
          </div>

          {activeTab === "credits" ? (
            <>
              {/* Credit Features */}
              <div className="mb-6">
                <h2 className="font-heading text-lg text-white mb-3">
                  {language === 'fr' ? 'Utilisez vos pièces' : 'Use your coins'}
                </h2>
                <div className="grid grid-cols-3 gap-3">
                  {FEATURES.map((feature) => (
                    <div 
                      key={feature.label}
                      className="bg-card/50 backdrop-blur-sm border border-white/5 rounded-2xl p-3 text-center"
                    >
                      <div className={`${feature.color} mb-2 flex justify-center`}>
                        {feature.icon}
                      </div>
                      <p className="text-xs text-white mb-1">{tx(feature.label)}</p>
                      <p className="text-[10px] text-muted-foreground">{feature.cost} {language === 'fr' ? 'pièces' : 'coins'}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Boost Button */}
              <Button
                onClick={handleBoost}
                disabled={boostLoading || (user?.credits || 0) < 20}
                className="w-full h-14 rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold text-base mb-6 hover:opacity-90"
                data-testid="boost-btn"
              >
                <Zap className="w-5 h-5 mr-2" />
                {boostLoading 
                  ? (language === 'fr' ? "Boost en cours..." : "Boosting...") 
                  : (language === 'fr' ? "Booster mon profil (20 pièces)" : "Boost my profile (20 coins)")}
              </Button>

              {/* Packages */}
              <div className="mb-6">
                <h2 className="font-heading text-lg text-white mb-3">
                  {language === 'fr' ? 'Acheter des pièces' : 'Buy coins'}
                </h2>
                <div className="space-y-3">
                  {packagesWithPrices.map((pkg) => (
                    <button
                      key={pkg.id}
                      onClick={() => { setSelectedPackage(pkg.id); if (!isGooglePlayAvailable && !isPlayStoreApp()) handlePurchase(pkg.id); }}
                      className={`w-full p-4 rounded-2xl border-2 transition-all flex items-center gap-4 ${
                        selectedPackage === pkg.id
                          ? "border-primary bg-primary/10"
                          : "border-white/10 bg-card/50 hover:border-white/20"
                      }`}
                      data-testid={`package-${pkg.id}`}
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        pkg.popular ? "gradient-gold" : "bg-muted"
                      }`}>
                        <Star className={`w-6 h-6 ${pkg.popular ? "text-black" : "text-muted-foreground"}`} />
                      </div>
                      
                      <div className="flex-1 text-left">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-white">{pkg.credits} {language === 'fr' ? 'pièces' : 'coins'}</span>
                          {pkg.bonus && (
                            <span className="px-2 py-0.5 bg-secondary/20 text-secondary text-xs rounded-full font-bold">
                              {pkg.bonus}
                            </span>
                          )}
                        </div>
                        <span className="text-sm text-muted-foreground">{pkg.label}{showPrices ? ` • ${pkg.price}` : ""}</span>
                      </div>
                      
                      <div className="text-right flex items-center justify-end">
                        {pkg.image && (
                          <img 
                            src={pkg.image} 
                            alt={`Pack ${pkg.credits} pièces`}
                            className="w-16 h-16 object-contain"
                          />
                        )}
                      </div>
                      
                      {selectedPackage === pkg.id && (
                        <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Purchase Button */}
              <Button
                onClick={() => handlePurchase()}
                disabled={loading}
                className="w-full h-14 rounded-2xl bg-primary text-white font-bold text-base hover:shadow-[0_0_20px_rgba(225,29,72,0.5)] transition-all"
                data-testid="purchase-btn"
              >
                {loading ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  `${language === 'fr' ? 'Acheter' : 'Buy'} ${packagesWithPrices.find(p => p.id === selectedPackage)?.credits} ${language === 'fr' ? 'pièces' : 'coins'}`
                )}
              </Button>
            </>
          ) : (
            <>
              {/* Subscription Plans */}
              <div className="mb-6">
                <h2 className="font-heading text-lg text-white mb-3">
                  {language === 'fr' ? 'Abonnements Premium' : 'Premium Subscriptions'}
                </h2>
                
                {/* Google Play Billing Status */}
                {!googlePlayLoading && (isGooglePlayAvailable || !isPlayStoreApp()) && (
                  <div className="mb-4 p-3 rounded-xl text-sm bg-green-500/10 border border-green-500/30 text-green-400" data-testid="payment-status-banner">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      {isGooglePlayAvailable
                        ? (language === 'fr' ? 'Paiement Google Play activé' : 'Google Play Billing enabled')
                        : (language === 'fr' ? 'Paiement sécurisé par carte bancaire · renouvellement automatique' : 'Secure card payment · automatic renewal')}
                    </div>
                  </div>
                )}

                {purchaseError && (
                  <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 inline mr-2" />
                    {purchaseError}
                  </div>
                )}
                
                <div className="space-y-4">
                  {SUBSCRIPTIONS_GOOGLE.map((sub) => (
                    <button
                      key={sub.id}
                      onClick={() => { setSelectedSubscription(sub.id); if (!isGooglePlayAvailable && !isPlayStoreApp()) handleSubscribe(sub.id); }}
                      className={`w-full p-4 rounded-2xl border-2 transition-all ${
                        selectedSubscription === sub.id
                          ? "border-yellow-500 bg-yellow-500/10"
                          : "border-white/10 bg-card/50 hover:border-white/20"
                      } ${sub.popular ? "ring-2 ring-yellow-500/30" : ""}`}
                    >
                      {sub.popular && (
                        <div className="text-center mb-2">
                          <span className="px-3 py-1 bg-yellow-500 text-black text-xs font-bold rounded-full">
                            {language === 'fr' ? 'MEILLEURE OFFRE' : 'BEST VALUE'}
                          </span>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl gradient-gold flex items-center justify-center">
                            <Crown className="w-6 h-6 text-black" />
                          </div>
                          <div className="text-left">
                            <p className="font-bold text-white">{sub.name[language]}</p>
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {sub.duration[language]}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          {showPrices && <p className="text-lg font-bold text-yellow-400" data-testid={`sub-price-${sub.id}`}>{sub.price}</p>}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        {sub.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                            <span className="text-muted-foreground text-xs">{feature[language]}</span>
                          </div>
                        ))}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Subscribe Button */}
              <Button
                onClick={() => handleSubscribe()}
                disabled={loading || !selectedSubscription}
                className="w-full h-14 rounded-2xl gradient-gold text-black font-bold text-base hover:opacity-90 transition-all disabled:opacity-50"
                data-testid="subscribe-btn"
              >
                {loading ? (
                  <div className="w-6 h-6 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Crown className="w-5 h-5 mr-2" />
                    {language === 'fr' ? "S'abonner maintenant" : "Subscribe now"}
                  </>
                )}
              </Button>
              
              <p className="text-center text-xs text-muted-foreground mt-3">
                {language === 'fr' 
                  ? `Vous avez ${user?.credits || 0} pièces disponibles`
                  : `You have ${user?.credits || 0} coins available`}
              </p>
            </>
          )}

          <p className="text-center text-xs text-muted-foreground mt-4">
            {language === 'fr' 
              ? 'Paiement sécurisé • Crédits ajoutés instantanément'
              : 'Secure payment • Credits added instantly'}
          </p>
        </div>
      </div>

      <BottomNav />

      {/* Review Prompt Modal */}
      <ReviewPromptModal
        isOpen={showReviewPrompt}
        onClose={closeReviewPrompt}
        onReviewGiven={handleReviewResponse}
        language={language}
        triggerSource={triggerSource}
      />
    </div>
  );
}
